import { type DashboardPeriod, DEFAULT_DASHBOARD_PERIOD } from "@repo/shared/types";
import { CheckCircle2, Eye, FilePen, GitBranch, Loader, PenLine } from "lucide-react";
import { useState } from "react";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { PeriodSwitch } from "./period-switch";
import { T } from "./pipeline-widget.i18n";
import { SegmentBar } from "./segment-bar";
import { StageTiles } from "./stage-tiles";
import { WidgetCard } from "./widget-card";

export function PipelineWidget({ target }: { target: DashboardTarget }) {
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);
  const { data, isLoading, error } = useDashboardWidget("pipeline", target, { period });
  const t = useT(T);
  const { count } = useDashboardFormat();

  return (
    <WidgetCard
      icon={GitBranch}
      title={t.pipeline}
      description={t.pipelineDescription}
      actions={<PeriodSwitch value={period} onChange={setPeriod} />}
      isLoading={isLoading}
      error={error}
    >
      {data && (
        <>
          {/* The bar shows the open flows; completed ones are the period's count. */}
          <SegmentBar
            segments={[
              { key: "draft", value: data.draft, tone: "muted" },
              { key: "inProgress", value: data.inProgress, tone: "info" },
              { key: "inReview", value: data.inReview, tone: "warning" },
              { key: "signing", value: data.signing, tone: "brand" },
            ]}
          />
          <StageTiles
            tiles={[
              {
                key: "draft",
                icon: FilePen,
                tone: "muted",
                count: count(data.draft),
                label: t.draft,
              },
              {
                key: "inProgress",
                icon: Loader,
                tone: "info",
                count: count(data.inProgress),
                label: t.inProgress,
              },
              {
                key: "inReview",
                icon: Eye,
                tone: "warning",
                count: count(data.inReview),
                label: t.inReview,
              },
              {
                key: "signing",
                icon: PenLine,
                tone: "brand",
                count: count(data.signing),
                label: t.signing,
              },
              {
                key: "completed",
                icon: CheckCircle2,
                tone: "success",
                count: count(data.completed),
                label: t.completed,
              },
            ]}
          />
        </>
      )}
    </WidgetCard>
  );
}
